import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, X, Newspaper, Image, Save, ExternalLink } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

type CoverageType = 'media' | 'video' | 'brand' | 'graduate';

interface MediaCoverageItem {
  id: number;
  title: string;
  type: CoverageType;
  typeLabel?: string;
  description?: string;
  imageUrl?: string;
  videoUrl?: string;
  link?: string;
  linkText?: string;
  date?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

const COVERAGE_TYPES: { value: CoverageType; label: string }[] = [
  { value: 'media', label: '媒體報導' },
  { value: 'video', label: '影片' },
  { value: 'brand', label: '品牌合作' },
  { value: 'graduate', label: '學員畢業' },
];

const emptyForm: Omit<MediaCoverageItem, 'id' | 'createdAt' | 'updatedAt'> = {
  title: '',
  type: 'media',
  typeLabel: '',
  description: '',
  imageUrl: '',
  videoUrl: '',
  link: '',
  linkText: '',
  date: '',
  isActive: true,
  sortOrder: 0,
};

const AdminMediaCoverage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<MediaCoverageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const { can } = usePermissions();
  const canWriteCms = can(PERMISSIONS.CMS_WRITE);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/public/admin/media-coverage');
      if (res.ok) {
        const data = await res.json();
        setItems(Array.isArray(data) ? data : data.items || []);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '獲取媒體報導失敗');
      }
    } catch {
      setError('獲取媒體報導失敗');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const openEdit = (item: MediaCoverageItem) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      type: item.type || 'media',
      typeLabel: item.typeLabel || '',
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      videoUrl: item.videoUrl || '',
      link: item.link || '',
      linkText: item.linkText || '',
      date: item.date || '',
      isActive: item.isActive ?? true,
      sortOrder: item.sortOrder ?? 0,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('標題為必填');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const payload = {
        title: form.title.trim(),
        type: form.type,
        typeLabel: form.typeLabel?.trim() || undefined,
        description: form.description?.trim() || undefined,
        imageUrl: form.imageUrl?.trim() || undefined,
        videoUrl: form.videoUrl?.trim() || undefined,
        link: form.link?.trim() || undefined,
        linkText: form.linkText?.trim() || undefined,
        date: form.date?.trim() || undefined,
        isActive: form.isActive,
        sortOrder: Number(form.sortOrder) || 0,
      };

      const res = editingId
        ? await apiFetch(`/api/public/admin/media-coverage/${editingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : await apiFetch('/api/public/admin/media-coverage', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

      if (res.ok) {
        setMessage(editingId ? '已更新媒體報導' : '已新增媒體報導');
        closeModal();
        await fetchItems();
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '儲存失敗');
      }
    } catch {
      setError('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: MediaCoverageItem) => {
    if (!confirm(`確定要刪除「${item.title}」嗎？此操作無法復原。`)) return;

    try {
      const res = await apiFetch(`/api/public/admin/media-coverage/${item.id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setMessage('已刪除媒體報導');
        await fetchItems();
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '刪除失敗');
      }
    } catch {
      setError('刪除失敗');
    }
  };

  const updateForm = (field: keyof typeof form, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const typeLabel = (type: CoverageType) =>
    COVERAGE_TYPES.find((t) => t.value === type)?.label || type;

  return (
    <AdminLayout currentPage="media-coverage">
      <div>
        {(error || message) && (
          <div
            className={`mb-4 p-4 rounded-lg flex justify-between items-center ${
              error
                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-red-700 dark:text-red-400'
                : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 text-green-700 dark:text-green-400'
            }`}
          >
            <span>{error || message}</span>
            <button
              onClick={() => {
                setError('');
                setMessage('');
              }}
              className="hover:opacity-70"
            >
              ✕
            </button>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Newspaper className="text-brand-600" size={22} />
              媒體報導管理
            </h2>
            {canWriteCms && (
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-2 px-4 py-2 text-white font-bold bg-brand-600 hover:bg-brand-700 rounded-lg shadow-sm transition-colors"
              >
                <Plus size={18} />
                新增報導
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" />
              加載中...
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Newspaper size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p>尚未建立任何媒體報導</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold">排序</th>
                    <th className="px-4 py-3 text-sm font-semibold">標題</th>
                    <th className="px-4 py-3 text-sm font-semibold">類型</th>
                    <th className="px-4 py-3 text-sm font-semibold">日期</th>
                    <th className="px-4 py-3 text-sm font-semibold">狀態</th>
                    <th className="px-4 py-3 text-sm font-semibold text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {item.sortOrder}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className="w-14 h-10 rounded object-cover border border-gray-200 dark:border-gray-600"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-14 h-10 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <Image size={16} />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-gray-800 dark:text-white">{item.title}</div>
                            {item.link && (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-0.5 text-xs text-brand-600 dark:text-brand-400 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink size={10} />
                                {item.linkText || '連結'}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                          {typeLabel(item.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {item.date || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.isActive
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {item.isActive ? '啟用' : '停用'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canWriteCms ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEdit(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 rounded hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
                            >
                              <Pencil size={14} />
                              編輯
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            >
                              <Trash2 size={14} />
                              刪除
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                  {editingId ? '編輯媒體報導' : '新增媒體報導'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    標題 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => updateForm('title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      類型
                    </label>
                    <select
                      value={form.type}
                      onChange={(e) => updateForm('type', e.target.value as CoverageType)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    >
                      {COVERAGE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      類型標籤
                    </label>
                    <input
                      type="text"
                      value={form.typeLabel}
                      onChange={(e) => updateForm('typeLabel', e.target.value)}
                      placeholder={typeLabel(form.type)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    描述
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      圖片 URL
                    </label>
                    <input
                      type="url"
                      value={form.imageUrl}
                      onChange={(e) => updateForm('imageUrl', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      影片 URL
                    </label>
                    <input
                      type="url"
                      value={form.videoUrl}
                      onChange={(e) => updateForm('videoUrl', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      連結
                    </label>
                    <input
                      type="url"
                      value={form.link}
                      onChange={(e) => updateForm('link', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      連結文字
                    </label>
                    <input
                      type="text"
                      value={form.linkText}
                      onChange={(e) => updateForm('linkText', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      日期
                    </label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => updateForm('date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      排序
                    </label>
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => updateForm('sortOrder', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => updateForm('isActive', e.target.checked)}
                      className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">啟用</span>
                  </label>
                </div>

                <div className="pt-4 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    取消
                  </button>
                  {canWriteCms && (
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-6 py-2.5 text-white font-bold bg-brand-600 hover:bg-brand-700 rounded-lg shadow-sm disabled:opacity-60 transition-colors"
                    >
                      {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                      {saving ? '儲存中...' : '儲存'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMediaCoverage;
